import { type FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { returnCasesHttp } from '@/services/returnCases'
import type {
  InternationalResourceResponse,
  InternationalWorkspace,
} from '@/types/returnCase.types'

export interface InternationalTaxPanelClient {
  internationalWorkspace: (caseId: string) => Promise<InternationalWorkspace>
  createInternationalResource: (
    caseId: string,
    path: string,
    input: Record<string, unknown>
  ) => Promise<InternationalResourceResponse>
  evaluateInternational: (
    caseId: string,
    input: Record<string, unknown>
  ) => Promise<InternationalResourceResponse>
}

interface Props {
  caseId: string
  canEdit: boolean
  sourceType?: 'CUSTOMER_MANUAL' | 'STAFF_MANUAL'
  client?: InternationalTaxPanelClient
  onChanged?: () => void | Promise<void>
}

type Field = {
  name: string
  label: string
  type?: string
  required?: boolean
  options?: Array<string | { value: string; label: string }>
  defaultValue?: string
}

type FormDefinition = { title: string; path: string; fields: Field[] }
const boolFields = new Set([
  'indian_citizen',
  'person_of_indian_origin',
  'visit_to_india',
  'left_india_for_employment',
  'indian_ship_crew',
  'liable_to_tax_elsewhere',
  'travel_history_complete',
  'prior_year_history_complete',
  'context_complete',
  'taxable_in_india',
  'disputed',
])

const text = (form: FormData, key: string) => String(form.get(key) ?? '').trim()

export default function InternationalTaxPanel({
  caseId,
  canEdit,
  sourceType = 'STAFF_MANUAL',
  client = returnCasesHttp,
  onChanged,
}: Props) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const workspace = useQuery({
    queryKey: ['international-tax', caseId],
    queryFn: () => client.internationalWorkspace(caseId),
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['international-tax', caseId] })
    await onChanged?.()
  }
  const create = useMutation({
    mutationFn: ({ path, input }: { path: string; input: Record<string, unknown> }) =>
      client.createInternationalResource(caseId, path, input),
    onSuccess: async () => {
      setError('')
      await refresh()
    },
    onError: cause =>
      setError(
        cause instanceof Error ? cause.message : 'The international-tax record was rejected'
      ),
  })
  const evaluate = useMutation({
    mutationFn: (input: Record<string, unknown>) => client.evaluateInternational(caseId, input),
    onSuccess: async () => {
      setError('')
      await refresh()
    },
    onError: cause =>
      setError(cause instanceof Error ? cause.message : 'The governed evaluation was rejected'),
  })

  if (workspace.isLoading) return <p>Loading residency and foreign reporting…</p>
  if (workspace.isError || !workspace.data) {
    return (
      <div className="alert alert-error" role="alert">
        Residency and foreign reporting are unavailable.
      </div>
    )
  }
  const data = workspace.data
  const ids = (rows: Array<{ id: string }>) => rows.map(row => row.id)
  const forms: FormDefinition[] = [
    {
      title: 'India presence interval',
      path: 'presence-intervals',
      fields: [
        { name: 'arrived_on', label: 'Arrived in India', type: 'date' },
        { name: 'departed_on', label: 'Departed India', type: 'date' },
        { name: 'arrival_port', label: 'Arrival port (optional)', required: false },
        { name: 'departure_port', label: 'Departure port (optional)', required: false },
      ],
    },
    {
      title: 'Prior-year residence summary',
      path: 'prior-year-summaries',
      fields: [
        { name: 'previous_year', label: 'Previous year (YYYY-YY)' },
        { name: 'india_days', label: 'Days in India', type: 'number' },
        { name: 'residential_status', label: 'Status', options: ['ROR', 'RNOR', 'NR'] },
      ],
    },
    {
      title: 'Residence context',
      path: 'residency-contexts',
      fields: [
        ...[
          ['indian_citizen', 'Indian citizen'],
          ['person_of_indian_origin', 'Person of Indian origin'],
          ['visit_to_india', 'Visit to India'],
          ['left_india_for_employment', 'Left India for employment'],
          ['indian_ship_crew', 'Indian ship crew'],
          ['liable_to_tax_elsewhere', 'Liable to tax elsewhere'],
          ['travel_history_complete', 'Travel history complete'],
          ['prior_year_history_complete', 'Prior-year history complete'],
          ['context_complete', 'Context review complete'],
        ].map(([name, label]) => ({ name, label, options: ['true', 'false'] })),
        {
          name: 'indian_source_total_income',
          label: 'Indian-source total income (₹)',
          type: 'number',
          defaultValue: '0',
        },
      ],
    },
    {
      title: 'Schedule FA asset or role',
      path: 'foreign-assets',
      fields: [
        {
          name: 'category',
          label: 'FA category',
          options: ['A1', 'A2', 'A3', 'A4', 'B', 'C', 'D', 'E', 'F', 'G'],
        },
        {
          name: 'asset_kind',
          label: 'Asset/role kind',
          options: [
            'DEPOSITORY_ACCOUNT',
            'CUSTODIAN_ACCOUNT',
            'EQUITY_OR_DEBT_INTEREST',
            'CASH_VALUE_INSURANCE_OR_ANNUITY',
            'FINANCIAL_INTEREST_IN_ENTITY',
            'IMMOVABLE_PROPERTY',
            'OTHER_CAPITAL_ASSET',
            'SIGNING_AUTHORITY_ACCOUNT',
            'FOREIGN_TRUST_ROLE',
            'OTHER_FOREIGN_INCOME',
          ],
        },
        { name: 'country_code', label: 'Country code', defaultValue: 'US' },
        { name: 'entity_name', label: 'Foreign institution/entity' },
        { name: 'address', label: 'Foreign address' },
        { name: 'identifier', label: 'Account/tax/entity identifier', type: 'password' },
        {
          name: 'owner_role',
          label: 'Relationship',
          options: [
                'LEGAL_OWNER',
                'JOINT_OWNER',
                'BENEFICIAL_OWNER',
            'BENEFICIARY',
            'SIGNING_AUTHORITY',
            'TRUSTEE',
            'SETTLOR',
          ],
        },
        { name: 'held_from', label: 'Held from', type: 'date' },
        { name: 'held_to', label: 'Held to (optional)', type: 'date', required: false },
      ],
    },
    {
      title: 'Foreign income',
      path: 'foreign-incomes',
      fields: [
        {
          name: 'foreign_asset_id',
          label: 'Linked FA asset (optional)',
          options: [{ value: '', label: 'None' }, ...ids(data.foreign_assets)],
          required: false,
        },
        { name: 'country_code', label: 'Country code', defaultValue: 'US' },
        {
          name: 'income_head',
          label: 'Income head',
          options: ['SALARY', 'HOUSE_PROPERTY', 'CAPITAL_GAINS', 'BUSINESS', 'OTHER_SOURCES'],
        },
        { name: 'nature', label: 'Nature of income' },
        { name: 'accrued_from', label: 'Accrued from', type: 'date' },
        { name: 'accrued_to', label: 'Accrued to', type: 'date' },
        { name: 'taxable_in_india', label: 'Taxable in India', options: ['true', 'false'] },
        { name: 'itr_schedule', label: 'ITR schedule' },
        { name: 'itr_item_reference', label: 'ITR item reference' },
      ],
    },
    {
      title: 'Foreign tax',
      path: 'foreign-taxes',
      fields: [
        { name: 'foreign_income_id', label: 'Foreign income', options: ids(data.foreign_incomes) },
        { name: 'tax_kind', label: 'Tax kind', options: ['WITHHELD', 'PAID', 'ASSESSED'] },
        { name: 'tax_date', label: 'Tax date', type: 'date' },
        { name: 'disputed', label: 'Disputed', options: ['false', 'true'] },
        {
          name: 'certificate_evidence_binding_id',
          label: 'Tax certificate evidence ID (optional)',
          required: false,
        },
        {
          name: 'payment_evidence_binding_id',
          label: 'Payment evidence ID (optional)',
          required: false,
        },
      ],
    },
    {
      title: 'Currency conversion lineage',
      path: 'fx-conversions',
      fields: [
        {
          name: 'parent_type',
          label: 'Parent type',
          options: ['FOREIGN_ASSET', 'FOREIGN_INCOME', 'FOREIGN_TAX', 'SCHEDULE_AL'],
        },
        { name: 'parent_id', label: 'Parent record ID' },
        {
          name: 'value_context',
          label: 'Value context',
          options: [
            'INITIAL',
            'PEAK',
            'CLOSING',
            'COST',
            'GROSS_INCOME',
            'GROSS_PROCEEDS',
            'FOREIGN_INCOME',
            'FOREIGN_TAX',
            'AL_COST',
            'AL_LIABILITY',
          ],
        },
        { name: 'original_amount', label: 'Original amount', type: 'number' },
        { name: 'currency_code', label: 'Currency', defaultValue: 'USD' },
        { name: 'rate', label: 'INR conversion rate', type: 'number' },
        {
          name: 'rate_type',
          label: 'Rate authority',
          options: ['SBI_TT_BUYING', 'MANUAL_CA_APPROVED'],
        },
        { name: 'rate_source', label: 'Rate source' },
        { name: 'rate_date', label: 'Rate date', type: 'date' },
        { name: 'rate_version', label: 'Rate version' },
        {
          name: 'override_reason',
          label: 'Manual-rate approval reason (optional)',
          required: false,
        },
      ],
    },
    {
      title: 'Foreign-tax relief claim',
      path: 'relief-claims',
      fields: [
        { name: 'foreign_income_id', label: 'Foreign income', options: ids(data.foreign_incomes) },
        { name: 'foreign_tax_id', label: 'Foreign tax', options: ids(data.foreign_taxes) },
        { name: 'relief_section', label: 'Section', options: ['90', '90A', '91'] },
        { name: 'treaty_reference', label: 'Treaty reference (90/90A)', required: false },
        { name: 'treaty_article', label: 'Treaty article (90/90A)', required: false },
        {
          name: 'indian_tax_on_corresponding_income',
          label: 'Indian tax on corresponding income',
          type: 'number',
          required: false,
        },
        {
          name: 'computation_revision',
          label: 'Current computation revision',
          type: 'number',
          required: false,
        },
        { name: 'computation_sha256', label: 'Current computation SHA-256', required: false },
      ],
    },
    {
      title: 'Form 67 history — record only',
      path: 'form67-history',
      fields: [
        { name: 'relief_claim_id', label: 'Relief claim', options: ids(data.relief_claims) },
        { name: 'status', label: 'External status', options: ['NOT_FILED', 'FILED_EXTERNALLY'] },
        { name: 'return_due_date', label: 'Return due date', type: 'date' },
        { name: 'filed_on', label: 'Filed on (external only)', type: 'date', required: false },
        {
          name: 'acknowledgement',
          label: 'Acknowledgement (external only)',
          type: 'password',
          required: false,
        },
        {
          name: 'form_evidence_binding_id',
          label: 'Form evidence ID (external only)',
          required: false,
        },
      ],
    },
    {
      title: 'Schedule AL asset or liability',
      path: 'schedule-al-items',
      fields: [
        { name: 'item_kind', label: 'Item kind', options: ['ASSET', 'LIABILITY'] },
        {
          name: 'category',
          label: 'Category',
          options: [
            'IMMOVABLE',
            'JEWELLERY_BULLION',
            'ART_COLLECTION',
            'VEHICLE_VESSEL_AIRCRAFT',
            'BANK_DEPOSIT',
            'SHARES_SECURITIES',
            'INSURANCE_POLICY',
            'LOANS_ADVANCES',
            'CASH_IN_HAND',
            'FIRM_AOP_INTEREST',
            'OTHER',
          ],
        },
        { name: 'location_scope', label: 'Location', options: ['INDIA', 'FOREIGN'] },
        { name: 'description', label: 'Description' },
        {
          name: 'related_asset_id',
          label: 'Related AL asset ID (required for liability)',
          required: false,
        },
        { name: 'amount_inr', label: 'INR value (India only)', type: 'number', required: false },
      ],
    },
  ]

  const submit = (definition: FormDefinition, form: HTMLFormElement) => {
    const values = new FormData(form)
    const input: Record<string, unknown> = {}
    for (const field of definition.fields) {
      const current = text(values, field.name)
      if (!current) continue
      input[field.name] = boolFields.has(field.name) ? current === 'true' : current
    }
    create.mutate({
      path: definition.path,
      input: {
        expected_revision: data.case_revision,
        source_type: sourceType,
        source_reference: text(values, 'source_reference') || `international-ui:${definition.path}`,
        reason: text(values, 'reason') || `Record ${definition.path} disclosure`,
        evidence_binding_id: text(values, 'evidence_binding_id') || undefined,
        ...input,
      },
    })
  }

  return (
    <section
      className="mt-6 rounded border border-border p-4"
      aria-label="International tax module"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Residency, foreign reporting, relief and Schedule AL</h2>
          <p className="text-sm text-text-secondary">
            Reporting calendar {data.reporting_calendar_start} to {data.reporting_calendar_end}.
            This workspace records Form 67 history but does not file forms or remit funds.
          </p>
        </div>
        <span className="badge">
          Rule {data.rule_version} · revision {data.case_revision}
        </span>
      </div>
      {error && (
        <div className="alert alert-error mt-3" role="alert">
          {error}
        </div>
      )}
      {data.warnings.length > 0 && (
        <div className="alert alert-warning mt-3" role="status">
          <strong>Professional review required</strong>
          <ul className="ml-5 list-disc">
            {data.warnings.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <Metric label="Presence intervals" value={data.presence_intervals.length} />
        <Metric label="FA records" value={data.foreign_assets.length} />
        <Metric label="Relief claims" value={data.relief_claims.length} />
        <Metric label="Schedule AL items" value={data.schedule_al_items.length} />
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="mb-2 text-left font-medium">Server-derived results</caption>
          <thead>
            <tr>
              <th>Evaluation</th>
              <th>Status</th>
              <th>Subject</th>
              <th>Rule and explanation</th>
            </tr>
          </thead>
          <tbody>
            {data.current_evaluations.map(item => (
              <tr key={item.id} className="border-t border-border align-top">
                <td className="py-2">{item.evaluation_kind}</td>
                <td>
                  <span className="badge">{item.result_status}</span>
                </td>
                <td>{item.evaluation_key}</td>
                <td>
                  <details>
                    <summary>{item.rule_version} · show reasons</summary>
                    <pre className="max-w-xl whitespace-pre-wrap text-xs">
                      {JSON.stringify(item.result_json, null, 2)}
                    </pre>
                  </details>
                </td>
              </tr>
            ))}
            {data.current_evaluations.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-text-secondary">
                  Add source records, then run the governed evaluation.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data.foreign_assets.length > 0 && (
        <p className="mt-4 text-sm">
          Stored foreign identifiers:{' '}
          {data.foreign_assets.map(item => item.identifier_masked).join(', ')}
        </p>
      )}

      {canEdit && (
        <div className="mt-6 space-y-3 border-t border-border pt-4">
          <h3 className="font-medium">Add accepted source records</h3>
          <p className="text-sm text-text-secondary">
            Corrections are append-only. Residency, disclosure coverage, relief and Schedule AL
            status are server-derived and cannot be typed here.
          </p>
          {forms.map(definition => (
            <details key={definition.path} className="rounded border border-border p-3">
              <summary className="cursor-pointer font-medium">{definition.title}</summary>
              <form
                className="mt-3 grid gap-3 sm:grid-cols-2"
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault()
                  submit(definition, event.currentTarget)
                }}
              >
                {definition.fields.map(field => (
                  <InputField key={field.name} field={field} />
                ))}
                <SourceFields />
                <button className="btn btn-secondary sm:col-span-2" disabled={create.isPending}>
                  Save immutable source record
                </button>
              </form>
            </details>
          ))}
          <details className="rounded border border-border p-3">
            <summary className="cursor-pointer font-medium">Run governed evaluation</summary>
            <form
              className="mt-3 grid gap-3 sm:grid-cols-2"
              onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault()
                const values = new FormData(event.currentTarget)
                const total = text(values, 'total_income_inr')
                const revision = text(values, 'computation_revision')
                const hash = text(values, 'computation_sha256')
                evaluate.mutate({
                  expected_revision: data.case_revision,
                  source_type: sourceType,
                  source_reference: 'international-ui:evaluate',
                  reason: 'Recompute governed residency, FA, relief and Schedule AL results',
                  ...(total && revision && hash
                    ? {
                        total_income_inr: total,
                        computation_revision: revision,
                        computation_sha256: hash,
                      }
                    : {}),
                })
              }}
            >
              <InputField
                field={{
                  name: 'total_income_inr',
                  label: 'Current total income (optional)',
                  type: 'number',
                  required: false,
                }}
              />
              <InputField
                field={{
                  name: 'computation_revision',
                  label: 'Computation revision (optional)',
                  type: 'number',
                  required: false,
                }}
              />
              <InputField
                field={{
                  name: 'computation_sha256',
                  label: 'Computation SHA-256 (optional)',
                  required: false,
                }}
              />
              <button
                className="btn btn-primary sm:col-span-2"
                disabled={evaluate.isPending || create.isPending}
              >
                Run server-derived international-tax evaluation
              </button>
            </form>
          </details>
        </div>
      )}
    </section>
  )
}

function InputField({ field }: { field: Field }) {
  const className = 'input w-full'
  return (
    <label className="text-sm">
      <span className="mb-1 block text-text-secondary">{field.label}</span>
      {field.options ? (
        <select
          name={field.name}
          className={className}
          required={field.required !== false}
          defaultValue={field.defaultValue}
        >
          {field.options.map(option => {
            const value = typeof option === 'string' ? option : option.value
            const label = typeof option === 'string' ? option : option.label
            return (
              <option key={value || 'empty'} value={value}>
                {label}
              </option>
            )
          })}
        </select>
      ) : (
        <input
          name={field.name}
          aria-label={field.label}
          className={className}
          type={field.type ?? 'text'}
          required={field.required !== false}
          defaultValue={field.defaultValue}
          step={field.type === 'number' ? 'any' : undefined}
          autoComplete={field.type === 'password' ? 'off' : undefined}
        />
      )}
    </label>
  )
}

function SourceFields() {
  return (
    <>
      <InputField
        field={{
          name: 'evidence_binding_id',
          label: 'Evidence binding ID (optional)',
          required: false,
        }}
      />
      <InputField
        field={{ name: 'source_reference', label: 'Source reference (optional)', required: false }}
      />
      <InputField field={{ name: 'reason', label: 'Reason (optional)', required: false }} />
    </>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-border p-3">
      <div className="text-xs text-text-secondary">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  )
}
